import React, { useEffect, useState } from "react";
import { Crown, ShieldCheck, Loader, CheckCircle2, XCircle, Info } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

const NAVY = "#0A2540";
const GREEN = "#047857";
const GREEN_DEEP = "#064E3B";
const GOLD = "#D4AF37";

const TIER_COPY = {
  gold:   { label: "Gold", color: GOLD,    bg: "#FEF3C7" },
  silver: { label: "Silver", color: "#1E40AF", bg: "#E0F2FE" },
  bronze: { label: "Bronze", color: "#7C2D12", bg: "#FFE4D2" },
};

/**
 * TalentCloudOptIn — candidate-side card mounted on the Account page.
 *
 * Lets a logged-in candidate opt in or out of being matched by Resmo Pro
 * recruiters. Backend enforces a quality gate (must have skill assessment +
 * a 60+ tailored resume + completed Honesty Check). Every flip writes an
 * audit_events row.
 *
 * Double opt-in by design: even after opting in, the candidate must accept
 * each recruiter intro request individually via email before contact info
 * is shared.
 */
export default function TalentCloudOptIn() {
  const [state, setState] = useState(null);   // { opted_in, eligibility, ... }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/recruiter/talent-cloud/me");
      setState(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load Talent Cloud status.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (target) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/recruiter/talent-cloud/opt-in", {
        opted_in: target,
        visibility_scope: target ? "verified_recruiters" : "none",
      });
      // Refresh full state (eligibility is computed server-side)
      await load();
      // Eat result so React doesn't complain about unused
      void data;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't update opt-in.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="rm-card-static p-4 flex items-center gap-2"
           style={{ background: "#F8FAFC", borderColor: "#CBD5E1" }}
           data-testid="talent-cloud-loading">
        <Loader size={14} className="animate-spin" style={{ color: NAVY }} />
        <span className="font-mono-rm text-[11px] uppercase tracking-widest">Loading Talent Cloud status…</span>
      </div>
    );
  }

  const eligibility = state?.eligibility || {};
  const tier = (eligibility.tier || "bronze").toLowerCase();
  const tierP = TIER_COPY[tier] || TIER_COPY.bronze;
  const ready = !!eligibility.all_requirements_met;
  const optedIn = !!state?.opted_in;

  return (
    <div
      className="rm-card-static p-5"
      style={{
        background: optedIn ? "#ECFDF5" : "white",
        borderColor: NAVY,
        boxShadow: `4px 4px 0 0 ${optedIn ? GREEN : NAVY}`,
      }}
      data-testid="talent-cloud-card"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div style={{ color: optedIn ? GREEN : NAVY, flexShrink: 0 }}>
          <Crown size={28} />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-mono-rm uppercase text-[10px] tracking-widest"
               style={{ color: optedIn ? GREEN_DEEP : NAVY, fontWeight: 700 }}>
            · Resmo Pro · Talent Cloud
          </div>
          <h3 className="font-display uppercase text-lg leading-tight"
              style={{ color: NAVY, letterSpacing: "-0.01em" }}
              data-testid="talent-cloud-title">
            {optedIn
              ? "You're in the Talent Cloud."
              : "Let verified recruiters find you."}
          </h3>
          <p className="text-[13px] mt-1 leading-snug" style={{ color: "#475569" }}>
            {optedIn
              ? "Recruiters who match your tier and skills can request an intro. They never see your contact info until you accept each request — opt out anytime."
              : "Opt in and Resmo Pro recruiters can match your skills against open requisitions. They see your tier, top skills, and most recent fit score — never your email — until you accept a specific intro request."}
          </p>

          {/* Eligibility checklist */}
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2"
               data-testid="talent-cloud-eligibility-grid">
            <EligibilityRow ok={eligibility.has_skill_assessment} label="Skill Assessment" />
            <EligibilityRow ok={eligibility.has_qualifying_analysis} label="Tailored resume scored 60+" />
            <EligibilityRow ok={eligibility.has_honesty_check} label="Honesty Check complete" />
          </div>

          <div className="flex items-center gap-2 flex-wrap mt-3">
            <span
              className="font-mono-rm uppercase text-[10px] tracking-widest px-2 py-0.5 rounded inline-flex items-center gap-1"
              style={{
                background: tierP.bg, color: tierP.color,
                border: `1px solid ${tierP.color}`, fontWeight: 700,
              }}
              data-testid="talent-cloud-tier"
            >
              <Crown size={10} /> {tierP.label} tier
            </span>
            {optedIn && state?.opted_in_at && (
              <span className="font-mono-rm uppercase text-[10px] tracking-widest"
                    style={{ color: "#94A3B8" }}>
                · Opted in {fmtAgo(state.opted_in_at)}
              </span>
            )}
          </div>

          {error && (
            <div className="rm-card-static p-2 mt-3 text-xs font-mono-rm"
                 style={{ background: "#FEE2E2", borderColor: "#B91C1C", color: "#991B1B" }}
                 data-testid="talent-cloud-error">
              · {error}
            </div>
          )}

          {!ready && !optedIn && (
            <div className="rm-card-static p-2 mt-3"
                 style={{ background: "#FEF3C7", borderColor: "#CA8A04" }}>
              <div className="flex items-start gap-2">
                <Info size={12} style={{ color: "#78350F", marginTop: 2 }} />
                <p className="text-[12px] leading-snug" style={{ color: "#78350F" }}>
                  Complete all three above to unlock opt-in. The bar is high
                  on purpose — every Talent Cloud profile carries the Verified
                  by Resmo seal.
                </p>
              </div>
            </div>
          )}

          <div className="mt-4">
            {optedIn ? (
              <button
                type="button"
                onClick={() => toggle(false)}
                disabled={busy}
                className="rm-btn text-xs px-3 py-2"
                style={{
                  background: "white", color: "#9F1239",
                  border: "2px solid #9F1239", fontWeight: 700,
                }}
                data-testid="talent-cloud-opt-out-btn"
              >
                {busy ? <Loader size={12} className="animate-spin" /> : <XCircle size={12} />}
                {busy ? "Updating…" : "Opt out"}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => toggle(true)}
                disabled={!ready || busy}
                className="rm-btn text-xs px-3 py-2"
                style={{
                  background: ready ? GREEN : "#CBD5E1",
                  color: "white",
                  border: `2px solid ${ready ? GREEN_DEEP : "#94A3B8"}`,
                  boxShadow: ready ? `3px 3px 0 0 ${NAVY}` : "none",
                  fontWeight: 700,
                  opacity: ready ? 1 : 0.7,
                  cursor: ready ? "pointer" : "not-allowed",
                }}
                data-testid="talent-cloud-opt-in-btn"
              >
                {busy ? <Loader size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
                {busy ? "Joining…" : "Join the Talent Cloud"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function EligibilityRow({ ok, label }) {
  return (
    <div className="rm-card-static p-2 flex items-center gap-2"
         style={{
           background: ok ? "#ECFDF5" : "#F8FAFC",
           borderColor: ok ? GREEN : "#E2E8F0",
         }}>
      {ok
        ? <CheckCircle2 size={14} style={{ color: GREEN }} />
        : <XCircle size={14} style={{ color: "#CBD5E1" }} />}
      <span className="text-[11.5px]" style={{ color: ok ? GREEN_DEEP : "#475569", fontWeight: ok ? 700 : 400 }}>
        {label}
      </span>
    </div>
  );
}

function fmtAgo(iso) {
  try {
    const t = new Date(iso).getTime();
    const m = Math.floor((Date.now() - t) / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch {
    return "";
  }
}
